import { useState } from 'react';
import { Timer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth';

/**
 * Demo Setting — SUPER ADMIN ONLY. Renders nothing for any other role.
 * ON  → 60-minute idle window and multiple windows stay signed in.
 * OFF → standard 15-minute idle timeout and single-session logout.
 * Turns itself off nightly at midnight Eastern.
 *
 * `view` only decides which PARTS are drawn, so the sidebar can keep the
 * "Demo mode on" pill in sight while the toggle and its explainer live inside
 * the collapsed Account row. The behaviour, the state and the toast are the
 * same in every case.
 *   full     → toggle + pill + explainer (what it has always drawn)
 *   controls → toggle + explainer
 *   pill     → the pill only, nothing when demo mode is off
 */
const DemoSessionToggle = ({ view = 'full' }) => {
    const { isSuperAdmin, demoLongSession, setDemoLongSession } = useAuth();
    const [busy, setBusy] = useState(false);

    if (!isSuperAdmin()) return null;

    const flip = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const next = await setDemoLongSession(!demoLongSession);
            toast.success(next ? 'Demo mode on — 60 min, multiple windows' : 'Demo mode off — normal 15 min, single session');
        } catch (e) {
            toast.error('Could not save the Demo Setting');
        } finally {
            setBusy(false);
        }
    };

    const pill = demoLongSession ? (
        <div
            className="mx-2 mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300"
            data-testid="demo-session-pill"
        >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Demo mode on
        </div>
    ) : null;

    if (view === 'pill') return pill;

    return (
        <div className={view === 'controls' ? '' : 'pt-3 mt-3 border-t border-slate-200 dark:border-slate-700'}
             data-testid="demo-session-block">
            <button
                onClick={flip}
                disabled={busy}
                className="sidebar-link w-full justify-between"
                data-testid="demo-session-toggle"
                aria-pressed={demoLongSession}
            >
                <div className="flex items-center gap-3 text-left">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <Timer size={18} className={demoLongSession ? 'text-amber-500' : ''} />}
                    <span className="text-sm leading-tight">Demo Setting: keep me signed in (60 min, multiple windows)</span>
                </div>
                <span
                    className={`shrink-0 w-9 h-5 rounded-full transition-colors relative ${demoLongSession ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                >
                    <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${demoLongSession ? 'translate-x-4' : 'translate-x-0.5'}`}
                    />
                </span>
            </button>
            {view === 'full' && pill}
            <p className="px-2 mt-2 text-[11px] leading-snug text-slate-500 dark:text-slate-400" data-testid="demo-session-help">
                {demoLongSession
                    ? 'Turning this off — or the automatic reset at midnight Eastern — puts the normal 15-minute timeout and single-session logout back right away, in this window and any other one you left open. Nobody is signed out at that moment.'
                    : 'Off: normal 15-minute idle timeout, and logging in elsewhere signs this window out. Only you see this setting.'}
            </p>
        </div>
    );
};

export default DemoSessionToggle;
